import { useQuery } from '@tanstack/react-query'
import { PackageX } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { useActiveTenant } from '../contexts/tenant'

type DefectRow = {
  productId: string; sku: string | null; productName: string | null
  quantity: number; updatedAt: string
}

export function DefeitoPage() {
  const tenantId = useActiveTenant()

  const { data: rows = [], isLoading } = useQuery<DefectRow[]>({
    queryKey: ['stock', 'defect', tenantId],
    queryFn: async () => (await api.get('/stock/defect', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const total = rows.reduce((s, r) => s + r.quantity, 0)

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Estoque de defeito</h2>
        <p className="text-sm text-gray-500">Produtos devolvidos com defeito — <b>retidos</b>, fora do estoque de venda. Alimentado pela API de devoluções.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <Card>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{rows.length}</p>
            <p className="text-sm text-gray-500">SKUs retidos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-2xl font-bold text-red-600">{total}</p>
            <p className="text-sm text-gray-500">Unidades no defeito</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <PackageX className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-500">Nenhum produto no estoque de defeito.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Unidades retidas</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Atualizado em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => (
                    <tr key={r.productId} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-mono font-medium text-gray-900">{r.sku ?? '—'}</td>
                      <td className="px-5 py-3 text-gray-700">{r.productName ?? '—'}</td>
                      <td className="px-5 py-3 text-right font-semibold text-red-600">{r.quantity}</td>
                      <td className="px-5 py-3 text-gray-500">{new Date(r.updatedAt).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
